import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Youtube, Music2, Loader2, RefreshCw, Trash2, Star, StarOff,
    EyeOff, Eye, Plus, FileText, Download, ExternalLink, Link2,
    Radio, Wand2, Share2, ImageIcon,
} from "lucide-react";
import { http, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/**
 * Admin panel — video hub management.
 *
 * Responsibilities:
 *  - Sync YouTube uploads (one-click)
 *  - Add TikTok video by URL
 *  - Edit metadata (description, transcript, tags)
 *  - Toggle featured hero + hidden flag
 *  - Connect/disconnect YouTube OAuth (for captions download)
 *  - Fetch transcript from YouTube
 */
export default function AdminVideos() {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [ytStatus, setYtStatus] = useState({ connected: false });
    const [editVideo, setEditVideo] = useState(null);
    const [tiktokOpen, setTiktokOpen] = useState(false);
    const [tiktokForm, setTiktokForm] = useState({ url: "", description: "" });
    const [tiktokBusy, setTiktokBusy] = useState(false);

    // External YouTube import (interview / conference from another channel)
    const [ytExtOpen, setYtExtOpen] = useState(false);
    const [ytExtForm, setYtExtForm] = useState({
        url: "",
        category: "interview",
        guest_name: "",
        guest_link: "",
    });
    const [ytExtBusy, setYtExtBusy] = useState(false);

    const [pingBusy, setPingBusy] = useState(false);
    const [refetchBusy, setRefetchBusy] = useState(false);
    const [cleanupBusy, setCleanupBusy] = useState(false);

    const refresh = () =>
        http.get("/admin/videos").then(({ data }) => setVideos(data || [])).catch(() => {}).finally(() => setLoading(false));

    const refreshYtStatus = () =>
        http.get("/admin/youtube/auth/status").then(({ data }) => setYtStatus(data || { connected: false })).catch(() => {});

    useEffect(() => {
        refresh();
        refreshYtStatus();
        // Handle "youtube_connected=1" flag after OAuth callback redirect
        const params = new URLSearchParams(window.location.search);
        if (params.get("youtube_connected") === "1") {
            toast.success("YouTube connecté ✨ Tu peux maintenant récupérer les transcriptions.");
            window.history.replaceState({}, "", "/admin?section=videos");
            refreshYtStatus();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [submittingSitemap, setSubmittingSitemap] = useState(false);

    const submitSitemapToBing = async () => {
        setSubmittingSitemap(true);
        try {
            const { data } = await http.post("/admin/indexing/submit-sitemap");
            toast.success(`${data.urls_pinged} URLs poussées à Bing/IndexNow ✨ Réindexation en cours (24-72h côté Bing).`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmittingSitemap(false);
        }
    };

    const syncYoutube = async () => {
        setSyncing(true);
        try {
            const { data } = await http.post("/admin/videos/sync-youtube");
            toast.success(`YouTube synchronisé : ${data.inserted} nouvelle(s), ${data.updated} mise(s) à jour.`);
            await refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSyncing(false);
        }
    };

    const connectYoutube = async () => {
        try {
            const { data } = await http.get("/admin/youtube/auth/start");
            window.location.href = data.authorization_url;
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const disconnectYoutube = async () => {
        if (!window.confirm("Déconnecter YouTube ? Les transcriptions ne seront plus téléchargeables automatiquement (celles déjà en base restent).")) return;
        try {
            await http.post("/admin/youtube/auth/disconnect");
            toast.success("YouTube déconnecté.");
            refreshYtStatus();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const fetchTranscript = async (v) => {
        toast.info(`Récupération de la transcription depuis YouTube…`);
        try {
            const { data } = await http.post(`/admin/videos/${v.id}/fetch-transcript`);
            toast.success(`Transcription récupérée (${data.language}, ${data.source}, ${data.length} caractères).`);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const toggleFeatured = async (v) => {
        try {
            await http.put(`/admin/videos/${v.id}`, { featured: !v.featured });
            toast.success(v.featured ? "Vidéo retirée de la une." : "Vidéo mise en une (hero /langage-de-lumiere).");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const toggleHidden = async (v) => {
        try {
            await http.put(`/admin/videos/${v.id}`, { hidden: !v.hidden });
            toast.success(v.hidden ? "Vidéo re-publiée." : "Vidéo masquée du site.");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const deleteVideo = async (v) => {
        if (!window.confirm(`Supprimer définitivement "${v.title}" ?`)) return;
        try {
            await http.delete(`/admin/videos/${v.id}`);
            toast.success("Vidéo supprimée.");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const refetchThumbnail = async (v) => {
        toast.info("Régénération de la vignette TikTok en cours…");
        try {
            await http.post(`/admin/videos/${v.id}/refetch-thumbnail`);
            toast.success("Vignette régénérée (format 1.91:1 avec fond flouté) ✨");
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const reshareToFacebook = async (v) => {
        if (!window.confirm(`Republier "${v.title}" sur la Page Facebook ?`)) return;        try {
            const { data } = await http.post("/admin/facebook/reshare", {
                source_type: v.source,
                source_id: v.id,
            });
            toast.success("Post FB en cours de publication ✨ (voir historique dans le status)");
            console.log("FB reshare queued:", data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const addTiktok = async () => {
        if (!tiktokForm.url.trim()) {
            toast.error("Colle l'URL de la vidéo TikTok.");
            return;
        }
        setTiktokBusy(true);
        try {
            await http.post("/admin/videos/tiktok", tiktokForm);
            toast.success("Vidéo TikTok ajoutée ✨");
            setTiktokOpen(false);
            setTiktokForm({ url: "", description: "" });
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setTiktokBusy(false);
        }
    };

    const addYouTubeExternal = async () => {
        if (!ytExtForm.url.trim()) {
            toast.error("Colle l'URL de la vidéo YouTube.");
            return;
        }
        setYtExtBusy(true);
        try {
            await http.post("/admin/videos/youtube", {
                url: ytExtForm.url.trim(),
                category: ytExtForm.category,
                guest_name: ytExtForm.guest_name.trim() || undefined,
                guest_link: ytExtForm.guest_link.trim() || undefined,
            });
            toast.success("Vidéo YouTube ajoutée ✨");
            setYtExtOpen(false);
            setYtExtForm({ url: "", category: "interview", guest_name: "", guest_link: "" });
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setYtExtBusy(false);
        }
    };

    const pingGoogle = async () => {
        setPingBusy(true);
        try {
            const { data } = await http.post("/admin/videos/ping-google");
            toast.success(`Google + IndexNow pingés : ${data.urls_pinged} URLs notifiées.`);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setPingBusy(false);
        }
    };

    const refetchAllTranscripts = async () => {
        const force = window.confirm(
            "Re-télécharger toutes les transcriptions YouTube ?\n\n" +
            "· OK : re-fetch également celles que tu as éditées manuellement (elles seront écrasées)\n" +
            "· Annuler : préserve tes éditions manuelles, re-fetch uniquement les autres"
        );
        setRefetchBusy(true);
        try {
            const { data } = await http.post(`/admin/videos/refetch-all-transcripts${force ? "?force=true" : ""}`);
            toast.success(
                `Transcriptions re-téléchargées : ${data.refetched} OK · ${data.skipped_manual} préservées (édition manuelle) · ${data.failed} échec(s).`,
                { duration: 8000 }
            );
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setRefetchBusy(false);
        }
    };

    const cleanupOldUrls = async () => {
        if (!window.confirm("Nettoyer les anciennes URLs joy2live.me dans les descriptions des vidéos ? (event-list → /evenements, lichtsprache → /reservation)")) return;
        setCleanupBusy(true);
        try {
            const { data } = await http.post("/admin/videos/cleanup-joy2live-urls");
            toast.success(
                `${data.videos_updated} vidéo(s) mises à jour · ${data.total_replacements} URL(s) remplacées.`,
                { duration: 6000 }
            );
            refresh();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setCleanupBusy(false);
        }
    };

    const stats = {
        total: videos.length,
        youtube: videos.filter((v) => v.source === "youtube").length,
        tiktok: videos.filter((v) => v.source === "tiktok").length,
        withTranscript: videos.filter((v) => v.has_transcript).length,
        featured: videos.filter((v) => v.featured).length,
        hidden: videos.filter((v) => v.hidden).length,
    };

    return (
        <div className="space-y-6" data-testid="admin-videos-panel">
            {/* YouTube connection status + sync */}
            <div className="rounded-3xl border border-border/60 bg-card p-6">
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="flex items-start gap-4">
                        <Youtube className="w-8 h-8 text-red-500 shrink-0 mt-0.5" />
                        <div>
                            <div className="font-serif text-xl mb-1">Chaîne YouTube</div>
                            {ytStatus.connected ? (
                                <p className="text-sm text-muted-foreground">
                                    Connectée · <span className="font-medium text-foreground">{ytStatus.email}</span>
                                </p>
                            ) : (
                                <p className="text-sm text-muted-foreground">
                                    Non connectée — cliquez pour activer la récupération automatique des transcriptions YouTube.
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {ytStatus.connected ? (
                            <Button variant="outline" onClick={disconnectYoutube} className="rounded-full" data-testid="youtube-disconnect-btn">
                                <Link2 className="w-4 h-4 mr-2" /> Déconnecter YouTube
                            </Button>
                        ) : (
                            <Button onClick={connectYoutube} className="rounded-full bg-red-500 text-white hover:bg-red-600" data-testid="youtube-connect-btn">
                                <Link2 className="w-4 h-4 mr-2" /> Connecter YouTube (transcriptions)
                            </Button>
                        )}
                        <Button onClick={syncYoutube} disabled={syncing} className="rounded-full bg-primary text-primary-foreground" data-testid="youtube-sync-btn">
                            {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                            Synchroniser les vidéos
                        </Button>
                    </div>
                </div>
            </div>

            {/* Add TikTok */}
            <div className="rounded-3xl border border-border/60 bg-card p-6">
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="flex items-start gap-4">
                        <Music2 className="w-8 h-8 text-secondary shrink-0 mt-0.5" />
                        <div>
                            <div className="font-serif text-xl mb-1">TikTok</div>
                            <p className="text-sm text-muted-foreground">
                                Ajout manuel — colle l'URL d'une vidéo TikTok, on récupère titre + miniature via oEmbed.
                            </p>
                        </div>
                    </div>
                    <Button onClick={() => setTiktokOpen(true)} className="rounded-full bg-secondary text-secondary-foreground" data-testid="tiktok-add-btn">
                        <Plus className="w-4 h-4 mr-2" /> Ajouter une vidéo TikTok
                    </Button>
                    <Button onClick={() => setYtExtOpen(true)} className="rounded-full bg-red-500 text-white hover:bg-red-600" data-testid="youtube-ext-add-btn">
                        <Plus className="w-4 h-4 mr-2" /> Ajouter une vidéo YouTube externe
                    </Button>
                </div>
            </div>

            {/* Maintenance actions */}
            <div className="rounded-3xl border border-border/60 bg-card p-6">
                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div className="flex items-start gap-4">
                        <Wand2 className="w-8 h-8 text-primary shrink-0 mt-0.5" />
                        <div>
                            <div className="font-serif text-xl mb-1">Actions de maintenance</div>
                            <p className="text-sm text-muted-foreground max-w-xl">
                                Ré-appliquer le formatage récent aux transcriptions, notifier Google et Bing du contenu à indexer, ou nettoyer les anciennes URLs joy2live dans les descriptions.
                            </p>
                        </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        <Button
                            variant="outline"
                            onClick={refetchAllTranscripts}
                            disabled={refetchBusy || !ytStatus.connected}
                            className="rounded-full"
                            data-testid="refetch-all-transcripts-btn"
                        >
                            {refetchBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                            Re-fetch toutes les transcriptions
                        </Button>
                        <Button
                            variant="outline"
                            onClick={cleanupOldUrls}
                            disabled={cleanupBusy}
                            className="rounded-full"
                            data-testid="cleanup-urls-btn"
                        >
                            {cleanupBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Wand2 className="w-4 h-4 mr-2" />}
                            Nettoyer les anciennes URLs
                        </Button>
                        <Button
                            variant="outline"
                            onClick={pingGoogle}
                            disabled={pingBusy}
                            className="rounded-full"
                            data-testid="ping-google-btn"
                        >
                            {pingBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Radio className="w-4 h-4 mr-2" />}
                            Re-pinger Google Indexing
                        </Button>
                        <Button
                            variant="outline"
                            onClick={submitSitemapToBing}
                            disabled={submittingSitemap}
                            className="rounded-full border-primary/40"
                            data-testid="submit-sitemap-btn"
                            title="Pousse les 122 URLs du sitemap à Bing/IndexNow (Yandex, Naver, Seznam, Yep) — accélère l'indexation Bing d'un nouveau site."
                        >
                            {submittingSitemap ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Radio className="w-4 h-4 mr-2" />}
                            Pousser à Bing / IndexNow
                        </Button>
                    </div>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                {[
                    { label: "Total", value: stats.total, tone: "primary" },
                    { label: "YouTube", value: stats.youtube, tone: "red-500" },
                    { label: "TikTok", value: stats.tiktok, tone: "secondary" },
                    { label: "Avec transcription", value: stats.withTranscript, tone: "primary" },
                    { label: "À la une", value: stats.featured, tone: "yellow-500" },
                    { label: "Masquées", value: stats.hidden, tone: "muted-foreground" },
                ].map((s) => (
                    <div key={s.label} className="rounded-2xl bg-card border border-border/60 p-4">
                        <div className="text-xs text-muted-foreground uppercase tracking-wider">{s.label}</div>
                        <div className="text-2xl font-serif mt-1">{s.value}</div>
                    </div>
                ))}
            </div>

            {/* Videos list */}
            {loading ? (
                <p className="text-muted-foreground text-center py-16">Chargement…</p>
            ) : videos.length === 0 ? (
                <p className="text-muted-foreground text-center py-16">Aucune vidéo. Clique sur "Synchroniser les vidéos" ci-dessus.</p>
            ) : (
                <div className="rounded-3xl border border-border/60 overflow-x-auto bg-card">
                    <table className="w-full text-sm table-fixed">
                        <thead className="bg-accent/30 text-left text-xs uppercase tracking-wider text-muted-foreground">
                            <tr>
                                <th className="p-3">Titre</th>
                                <th className="p-3 w-20 hidden md:table-cell">Source</th>
                                <th className="p-3 w-16 hidden md:table-cell">Durée</th>
                                <th className="p-3 w-24 hidden lg:table-cell">Publiée</th>
                                <th className="p-3 w-28 hidden lg:table-cell">Transcript</th>
                                <th className="p-3 w-64 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody data-testid="admin-videos-table">
                            {videos.map((v) => (
                                <tr key={v.id} className={`border-t border-border/40 ${v.hidden ? "opacity-50" : ""}`} data-testid={`admin-video-row-${v.id}`}>
                                    <td className="p-3 align-top">
                                        <div className="flex items-start gap-3 min-w-0">
                                            {v.thumbnail_url && (
                                                <img src={v.thumbnail_url} alt="" loading="lazy" className="w-20 h-12 object-cover rounded shrink-0" />
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <div className="font-medium line-clamp-2 break-words">
                                                    {v.featured && <Star className="w-3 h-3 inline text-yellow-500 mr-1 fill-yellow-500" />}
                                                    {v.title}
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-1 truncate">/{v.slug}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-3 align-top hidden md:table-cell">
                                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${v.source === "youtube" ? "bg-red-500/10 text-red-500" : "bg-secondary/10 text-secondary"}`}>
                                            {v.source === "youtube" ? <Youtube className="w-3 h-3" /> : <Music2 className="w-3 h-3" />}
                                            {v.source === "youtube" ? "YouTube" : "TikTok"}
                                        </span>
                                    </td>
                                    <td className="p-3 align-top hidden md:table-cell font-mono text-xs">{v.duration_human || "—"}</td>
                                    <td className="p-3 align-top hidden lg:table-cell text-xs text-muted-foreground">
                                        {v.published_at ? new Date(v.published_at).toLocaleDateString("fr-FR") : "—"}
                                    </td>
                                    <td className="p-3 align-top hidden lg:table-cell">
                                        {v.has_transcript ? (
                                            <span className="text-xs text-green-600 flex items-center gap-1">
                                                <FileText className="w-3 h-3" /> {v.transcript_source || "présent"}
                                            </span>
                                        ) : (
                                            <span className="text-xs text-muted-foreground">—</span>
                                        )}
                                    </td>
                                    <td className="p-3 align-top text-right whitespace-nowrap">
                                        <div className="inline-flex gap-0.5 justify-end items-center">
                                            <button
                                                title={v.featured ? "Retirer de la une" : "Mettre à la une"}
                                                onClick={() => toggleFeatured(v)}
                                                className="p-1.5 rounded-full hover:bg-accent"
                                                data-testid={`toggle-featured-${v.id}`}
                                            >
                                                {v.featured ? <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" /> : <StarOff className="w-3.5 h-3.5 text-muted-foreground" />}
                                            </button>
                                            <button
                                                title={v.hidden ? "Ré-afficher" : "Masquer du site"}
                                                onClick={() => toggleHidden(v)}
                                                className="p-1.5 rounded-full hover:bg-accent"
                                                data-testid={`toggle-hidden-${v.id}`}
                                            >
                                                {v.hidden ? <EyeOff className="w-3.5 h-3.5 text-muted-foreground" /> : <Eye className="w-3.5 h-3.5 text-muted-foreground" />}
                                            </button>
                                            {v.source === "youtube" && (
                                                <button
                                                    title="Récupérer la transcription depuis YouTube"
                                                    onClick={() => fetchTranscript(v)}
                                                    disabled={!ytStatus.connected}
                                                    className="p-1.5 rounded-full hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed"
                                                    data-testid={`fetch-transcript-${v.id}`}
                                                >
                                                    <Download className="w-3.5 h-3.5 text-primary" />
                                                </button>
                                            )}
                                            {v.source === "tiktok" && (
                                                <button
                                                    title="Régénérer la vignette (format 1.91:1 pour le site + Facebook)"
                                                    onClick={() => refetchThumbnail(v)}
                                                    className="p-1.5 rounded-full hover:bg-accent"
                                                    data-testid={`refetch-thumb-${v.id}`}
                                                >
                                                    <ImageIcon className="w-3.5 h-3.5 text-primary" />
                                                </button>
                                            )}
                                            <button
                                                title="Éditer"
                                                onClick={() => setEditVideo(v)}
                                                className="p-1.5 rounded-full hover:bg-accent"
                                                data-testid={`edit-video-${v.id}`}
                                            >
                                                <FileText className="w-3.5 h-3.5 text-primary" />
                                            </button>
                                            <button
                                                title="Republier sur Facebook"
                                                onClick={() => reshareToFacebook(v)}
                                                className="p-1.5 rounded-full hover:bg-accent"
                                                data-testid={`reshare-fb-${v.id}`}
                                            >
                                                <Share2 className="w-3.5 h-3.5 text-blue-600" />
                                            </button>
                                            <a
                                                title="Ouvrir sur le site"
                                                href={`/transmissions/${v.slug}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="p-1.5 rounded-full hover:bg-accent inline-flex"
                                            >
                                                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
                                            </a>
                                            <button
                                                title="Supprimer"
                                                onClick={() => deleteVideo(v)}
                                                className="p-1.5 rounded-full hover:bg-red-500/10"
                                                data-testid={`delete-video-${v.id}`}
                                            >
                                                <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* TikTok modal */}
            <Dialog open={tiktokOpen} onOpenChange={setTiktokOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Ajouter une vidéo TikTok</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="tt-url">URL de la vidéo TikTok</Label>
                            <Input
                                id="tt-url"
                                placeholder="https://www.tiktok.com/@foundation.of.light/video/..."
                                value={tiktokForm.url}
                                onChange={(e) => setTiktokForm({ ...tiktokForm, url: e.target.value })}
                                data-testid="tiktok-url-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Titre, miniature et lien seront récupérés automatiquement. Tu peux ajouter une description SEO ci-dessous.
                            </p>
                        </div>
                        <div>
                            <Label htmlFor="tt-desc">Description (optionnelle, riche SEO)</Label>
                            <Textarea
                                id="tt-desc"
                                rows={4}
                                placeholder="Une invitation à ressentir la vibration du langage de lumière…"
                                value={tiktokForm.description}
                                onChange={(e) => setTiktokForm({ ...tiktokForm, description: e.target.value })}
                                data-testid="tiktok-desc-input"
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setTiktokOpen(false)} className="rounded-full">Annuler</Button>
                            <Button onClick={addTiktok} disabled={tiktokBusy} className="rounded-full bg-secondary text-secondary-foreground" data-testid="tiktok-submit-btn">
                                {tiktokBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                Ajouter
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit modal */}
            <EditVideoDialog
                video={editVideo}
                onClose={() => setEditVideo(null)}
                onSaved={() => { setEditVideo(null); refresh(); }}
            />

            {/* YouTube external import modal */}
            <Dialog open={ytExtOpen} onOpenChange={setYtExtOpen}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>Ajouter une vidéo YouTube externe</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label htmlFor="yt-ext-url">URL de la vidéo YouTube</Label>
                            <Input
                                id="yt-ext-url"
                                placeholder="https://youtu.be/... ou https://youtube.com/live/..."
                                value={ytExtForm.url}
                                onChange={(e) => setYtExtForm({ ...ytExtForm, url: e.target.value })}
                                data-testid="yt-ext-url-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                Pour les interviews / conférences enregistrées sur la chaîne d'un·e invité·e.
                            </p>
                        </div>
                        <div className="rounded-xl border border-border/60 p-3 space-y-2 bg-accent/20">
                            <Label className="text-sm font-medium">Classification</Label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="radio"
                                        name="yt-ext-cat"
                                        checked={ytExtForm.category === "interview"}
                                        onChange={() => setYtExtForm({ ...ytExtForm, category: "interview" })}
                                    />
                                    Interview / Conférence
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="radio"
                                        name="yt-ext-cat"
                                        checked={ytExtForm.category === "transmission"}
                                        onChange={() => setYtExtForm({ ...ytExtForm, category: "transmission" })}
                                    />
                                    Transmission
                                </label>
                            </div>
                        </div>
                        {ytExtForm.category === "interview" && (
                            <>
                                <div>
                                    <Label htmlFor="yt-ext-guest">Intervenant·e</Label>
                                    <Input
                                        id="yt-ext-guest"
                                        placeholder="Ex : Joëlle Thomas"
                                        value={ytExtForm.guest_name}
                                        onChange={(e) => setYtExtForm({ ...ytExtForm, guest_name: e.target.value })}
                                        data-testid="yt-ext-guest-input"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="yt-ext-link">Lien externe (site, chaîne) · backlink SEO</Label>
                                    <Input
                                        id="yt-ext-link"
                                        type="url"
                                        placeholder="https://..."
                                        value={ytExtForm.guest_link}
                                        onChange={(e) => setYtExtForm({ ...ytExtForm, guest_link: e.target.value })}
                                        data-testid="yt-ext-link-input"
                                    />
                                </div>
                            </>
                        )}
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setYtExtOpen(false)} className="rounded-full">Annuler</Button>
                            <Button onClick={addYouTubeExternal} disabled={ytExtBusy} className="rounded-full bg-red-500 text-white hover:bg-red-600" data-testid="yt-ext-submit-btn">
                                {ytExtBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                Ajouter
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function EditVideoDialog({ video, onClose, onSaved }) {
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!video) { setForm(null); return; }
        // Fetch the full public payload (includes transcript + description without <!-- fol:override --> marker cleaning)
        http.get(`/videos/${video.slug}`).then(({ data }) => {
            const cleanDesc = (data.description || "").replace(/^<!-- fol:override -->\n?/, "");
            setForm({
                title: data.title || "",
                description: cleanDesc,
                transcript: data.transcript || "",
                tags: (data.tags || []).join(", "),
                category: data.category || "transmission",
                guest_name: data.guest_name || "",
                guest_link: data.guest_link || "",
            });
        }).catch(() => {
            setForm({
                title: video.title || "",
                description: "",
                transcript: "",
                tags: (video.tags || []).join(", "),
                category: video.category || "transmission",
                guest_name: video.guest_name || "",
                guest_link: video.guest_link || "",
            });
        });
    }, [video]);

    if (!video) return null;

    const save = async () => {
        setSaving(true);
        try {
            await http.put(`/admin/videos/${video.id}`, {
                title: form.title,
                description: form.description,
                transcript: form.transcript,
                tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
                category: form.category,
                guest_name: form.category === "interview" ? (form.guest_name || "").trim() : "",
                guest_link: form.category === "interview" ? (form.guest_link || "").trim() : "",
            });
            toast.success("Vidéo mise à jour.");
            onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!video} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Éditer la vidéo</DialogTitle>
                </DialogHeader>
                {form && (
                    <div className="space-y-4" data-testid="edit-video-form">
                        <div>
                            <Label htmlFor="ev-title">Titre (affecte le slug SEO)</Label>
                            <Input id="ev-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="edit-title-input" />
                        </div>
                        {/* Classification : Transmission ou Interview / Conférence */}
                        <div className="rounded-xl border border-border/60 p-4 space-y-3 bg-accent/20">
                            <Label className="text-sm font-medium">Classification</Label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="radio"
                                        name="ev-category"
                                        value="transmission"
                                        checked={form.category === "transmission"}
                                        onChange={() => setForm({ ...form, category: "transmission" })}
                                        data-testid="edit-category-transmission"
                                    />
                                    Transmission
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer text-sm">
                                    <input
                                        type="radio"
                                        name="ev-category"
                                        value="interview"
                                        checked={form.category === "interview"}
                                        onChange={() => setForm({ ...form, category: "interview" })}
                                        data-testid="edit-category-interview"
                                    />
                                    Interview / Conférence
                                </label>
                            </div>
                            {form.category === "interview" && (
                                <div className="space-y-3 pt-2">
                                    <div>
                                        <Label htmlFor="ev-guest" className="text-xs text-muted-foreground">Intervenant·e</Label>
                                        <Input
                                            id="ev-guest"
                                            placeholder="Ex : Joëlle Thomas"
                                            value={form.guest_name}
                                            onChange={(e) => setForm({ ...form, guest_name: e.target.value })}
                                            data-testid="edit-guest-name-input"
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="ev-guest-link" className="text-xs text-muted-foreground">
                                            Lien externe (site, chaîne, page) · pour les backlinks SEO
                                        </Label>
                                        <Input
                                            id="ev-guest-link"
                                            type="url"
                                            placeholder="https://exemple.com/..."
                                            value={form.guest_link}
                                            onChange={(e) => setForm({ ...form, guest_link: e.target.value })}
                                            data-testid="edit-guest-link-input"
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <Label htmlFor="ev-desc">Description (affichée sous la vidéo)</Label>
                            <Textarea
                                id="ev-desc"
                                rows={10}
                                value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })}
                                data-testid="edit-desc-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                💡 Toute modification ici verrouille cette description : les prochaines synchronisations YouTube ne l'écraseront plus.
                            </p>
                        </div>
                        <div>
                            <Label htmlFor="ev-tags">Tags (séparés par des virgules)</Label>
                            <Input id="ev-tags" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="langage de lumière, méditation, cercle" data-testid="edit-tags-input" />
                        </div>
                        <div>
                            <Label htmlFor="ev-transcript">Transcription (retravaillée si besoin)</Label>
                            <Textarea
                                id="ev-transcript"
                                rows={14}
                                value={form.transcript}
                                onChange={(e) => setForm({ ...form, transcript: e.target.value })}
                                placeholder="Colle ou corrige ici la transcription…"
                                data-testid="edit-transcript-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">
                                🎙️ Astuce : clique sur le bouton <Download className="w-3 h-3 inline" /> dans la liste pour la récupérer automatiquement depuis YouTube, puis viens la retoucher ici si besoin.
                            </p>
                        </div>
                        <div className="flex justify-end gap-2 pt-4 border-t border-border/40">
                            <Button variant="outline" onClick={onClose} className="rounded-full">Annuler</Button>
                            <Button onClick={save} disabled={saving} className="rounded-full bg-primary text-primary-foreground" data-testid="edit-save-btn">
                                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                                Enregistrer
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
